import { Box, Divider } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useRouter } from 'next/router';

import { useUserByWhereQuery } from '@/__generated__/public-graphql';
import Button from '@/contents/General/Button';
import Icon from '@/contents/General/Icon';
import { NotEntered } from '@/contents/General/Panels';
import Stack from '@/contents/General/Stack';
import Typography from '@/contents/General/Typography';
import CampSiteCard from '@/modules/CampSite/CampSiteCard';
import useCampSiteLikeFromPublic from '@/modules/CampSiteLikeList/public/useCampSiteLikeFromPublic';
import useGetLoginUser from '@/modules/User/user/useGetLoginUser';
import theme from '@/styles/theme';

const Content = styled(Box)`
  position: relative;
  margin-top: 57px;
  padding: 0 ${theme.spacing(3)};
  z-index: 1;
`;

export const Favorite = ({ user_id }: { user_id: string }) => {
  const router = useRouter();
  const nextPage = (path: string) => {
    router.push(`/camper/${user_id}/favorite/${path}`);
  };
  const { user } = useGetLoginUser();
  const { data: publicUserData } = useUserByWhereQuery({
    where: {
      id: {
        _eq: user_id,
      },
    },
  });
  const publicUser = publicUserData?.user_public[0];
  const myReview = publicUser?.id === user?.id;

  const { data, isLoading } = useCampSiteLikeFromPublic(user_id, {});
  const allCampSiteIds =
    data?.all.map((campsite) => campsite.campsite_id) ?? [];
  const allCampSiteCount = data?.all_count.aggregate?.count ?? 0;
  const iWantToGoCount = data?.i_want_to_go_count.aggregate?.count ?? 0;
  const specialCount = data?.special_count.aggregate?.count ?? 0;

  return (
    <>
      <Content>
        <Stack center paddingTop={4} paddingBottom={3}>
          <Typography fontSize={21} fontWeight={700}>
            お気に入り
          </Typography>
        </Stack>
        <Box paddingBottom={5}>
          <Typography fontSize={18} fontWeight={700} paddingBottom={3}>
            最近お気に入りしたキャンプ場
          </Typography>
          {!isLoading && (
            <>
              {allCampSiteIds.length === 0 ? (
                <>
                  <NotEntered>
                    <Stack center column sx={{ height: '100%' }}>
                      <Typography
                        fontSize={16}
                        fontWeight={700}
                        color="white"
                        paddingBottom={3}
                      >
                        まだお気に入り登録がありません…
                      </Typography>
                      <Button
                        onClick={() => router.push('/search')}
                        buttonSize="md"
                        color="secondary"
                        sx={{ width: '246px' }}
                      >
                        キャンプ場を探す
                      </Button>
                    </Stack>
                  </NotEntered>
                </>
              ) : (
                <Stack
                  space={2}
                  sx={{
                    overflowX: 'scroll',
                    '&::-webkit-scrollbar': {
                      display: 'none',
                    },
                  }}
                >
                  {allCampSiteIds.map((id) => (
                    <CampSiteCard
                      key={id}
                      campSiteId={id}
                      cardSize="sm"
                      variant="normal"
                    />
                  ))}
                </Stack>
              )}
            </>
          )}
        </Box>
        <Box paddingBottom={5}>
          <Stack paddingBottom={3} between>
            <Typography fontSize={18} fontWeight={700}>
              {myReview
                ? '自分のリスト'
                : `${publicUser?.display_name}さんのリスト`}
            </Typography>
          </Stack>
          <Divider />
          <Stack
            alignCenter
            between
            padding={`${theme.spacing(2)} ${theme.spacing(1)}`}
            onClick={() => nextPage('all')}
            sx={{ cursor: 'pointer' }}
          >
            <Stack alignCenter space={2}>
              <Icon iconType="favorite-base" color="secondary" size={26} />
              <Box>
                <Typography fontSize={16} fontWeight={500}>
                  すべてのお気に入り
                </Typography>
              </Box>
            </Stack>
            <Typography fontSize={12} fontWeight={500} color="gray">
              {allCampSiteCount}ヶ所
            </Typography>
          </Stack>
          <Divider />
          <Stack
            alignCenter
            between
            padding={`${theme.spacing(2)} ${theme.spacing(1)}`}
            onClick={() => nextPage('i-want-to-go')}
            sx={{ cursor: 'pointer' }}
          >
            <Stack alignCenter space={2}>
              <Icon iconType="favorite-flag" />
              <Box>
                <Typography fontSize={16} fontWeight={500}>
                  行ってみたい
                </Typography>
              </Box>
            </Stack>
            <Typography fontSize={12} fontWeight={500} color="gray">
              {iWantToGoCount}ヶ所
            </Typography>
          </Stack>
          <Divider />
          <Stack
            alignCenter
            between
            padding={`${theme.spacing(2)} ${theme.spacing(1)}`}
            onClick={() => nextPage('special')}
            sx={{ cursor: 'pointer' }}
          >
            <Stack alignCenter space={2}>
              <Icon iconType="favorite-star" />
              <Box>
                <Typography fontSize={16} fontWeight={500}>
                  スペシャル
                </Typography>
              </Box>
            </Stack>
            <Typography fontSize={12} fontWeight={500} color="gray">
              {specialCount}ヶ所
            </Typography>
          </Stack>
          <Divider />
        </Box>
      </Content>
    </>
  );
};
